import { ICommonSettings, IInversCloudBoxxSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IInversCloudBoxxSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IInversCloudBoxxSetting;
}

export interface IInversCloudBoxxSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingInversCloudBoxxId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class InversCloudBoxxSettings extends React.PureComponent<
  IInversCloudBoxxSettingsProps,
  IInversCloudBoxxSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IInversCloudBoxxSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IInversCloudBoxxSettingsProps & IWithSDProps) {
    if (prevProps.editingInversCloudBoxxId !== this.props.editingInversCloudBoxxId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingInversCloudBoxxId) return;

    const editingConfig = this.props.commonSettings.inversCloudBoxxSettings?.find(
      (x) => x.id === this.props.editingInversCloudBoxxId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const inversSettings = this.props.commonSettings?.inversCloudBoxxSettings;
    if (!inversSettings) return;

    const isSettingAlreadyExist = inversSettings.some(
      (x) => x.name === value && x.id !== this.props.editingInversCloudBoxxId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (isSettingAlreadyExist) throw new Error("Invers CloudBoxx Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IInversCloudBoxxSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      apiBaseUrl: data.apiBaseUrl?.trim(),
      apiKey: data.apiKey?.trim()
    };
    if (this.props.editingInversCloudBoxxId) {
      obj.id = this.props.editingInversCloudBoxxId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "inversCloudBoxxSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "inversCloudBoxxSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingInversCloudBoxxId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingInversCloudBoxxId || "",
      configKey: "inversCloudBoxxSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Invers CloudBoxx setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Invers CloudBoxx setting"
        secondHeader="Invers CloudBoxx setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingInversCloudBoxxId ? this.deleteSetting : undefined}
            deleteTestId="inversCloudBoxxSettingDeleteButton"
            submitTestId="inversCloudBoxxSettingCreateButton"
            submitLabel={this.props.editingInversCloudBoxxId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configInversCloudBoxxForm"
          />
        }
      >
        <Form
          id="configInversCloudBoxxForm"
          name="inversCloudBoxxSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription(
              "inverscloudboxxsettingspopup_inverscloudboxxsettings_description"
            )}
          >
            <ConfigField
              label="Name"
              required
              tooltipContentKey="inverscloudboxxsettingspopup_inverscloudboxxsettings_name"
            >
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Invers CloudBoxx Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Invers CloudBoxx Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="inversCloudBoxxSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Api Base Url"
              required
              tooltipContentKey="inverscloudboxxsettingspopup_inverscloudboxxsettings_apibaseurl"
            >
              <Form.Item name="apiBaseUrl" rules={[{ required: true, message: "Please enter a Api Base Url." }]}>
                <KuikaInput size="md" data-testid="inversCloudBoxxSettingApiBaseUrlInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Api Key"
              required
              tooltipContentKey="inverscloudboxxsettingspopup_inverscloudboxxsettings_apikey"
            >
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Api Key." }]}>
                <KuikaInput size="md" data-testid="inversCloudBoxxSettingApiKeyInput" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
